import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Grid,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../context/SettingsContext';
import { useStripeApi } from '../hooks/useStripeApi';
import {
  fetchOpenInvoices,
  sendInvoiceReminder,
  runAllReminders,
  createRecurringSubscription,
  createAndSendInvoice,
  listStripePrices,
  type OpenInvoice,
} from '../services/stripeBillingApi';
import { listStripeCustomersResolved } from '../services/stripeResolved';
import { useBackendAuth } from '../services/apiClient';
import { stripeDashboardBase } from '../services/stripeApi';

function fmtMoney(cents: number, cur: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.toUpperCase() }).format(cents / 100);
}

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

const BillingPage: React.FC = () => {
  const { settings, isSecretConfigured } = useAppSettings();
  const { ready: stripeReady, localSecretKey } = useStripeApi();
  const navigate = useNavigate();
  const backend = useBackendAuth;
  const billing = settings.integrations.stripe.billing;
  const dashBase = localSecretKey ? stripeDashboardBase(localSecretKey) : 'https://dashboard.stripe.com';

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; label: string }>>([]);
  const [prices, setPrices] = useState<Array<{ id: string; nickname: string; amount: number; currency: string; interval: string }>>([]);
  const [form, setForm] = useState({ customerId: '', amount: '', description: 'Monthly service', priceId: '' });

  const load = useCallback(async () => {
    if (!stripeReady) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [inv, cust, pr] = await Promise.all([
        fetchOpenInvoices(settings),
        listStripeCustomersResolved(settings, isSecretConfigured, { limit: 50 }).then((r) =>
          r.data.map((c) => ({
            id: c.id,
            label: [c.name, c.email].filter(Boolean).join(' · ') || c.id,
          }))
        ),
        listStripePrices(settings),
      ]);
      setInvoices(inv);
      setCustomers(cust);
      setPrices(pr);
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Load failed' });
    } finally {
      setLoading(false);
    }
  }, [settings, isSecretConfigured, stripeReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const remind = async (id: string) => {
    setBusy(id);
    setMessage(null);
    try {
      await sendInvoiceReminder(settings, id);
      setMessage({ type: 'success', text: 'Reminder sent' });
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setBusy(null);
    }
  };

  const runReminders = async () => {
    if (!backend) {
      setMessage({ type: 'error', text: 'Bulk reminders require the Cloud Run backend (SMTP + logging)' });
      return;
    }
    setBusy('all');
    try {
      const r = await runAllReminders();
      setMessage({ type: 'success', text: `Sent ${r.sent} reminders · skipped ${r.skipped}` });
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setBusy(null);
    }
  };

  if (!stripeReady) {
    return (
      <Box>
        <Hero />
        <Alert severity="warning" action={<Button onClick={() => navigate('/settings#stripe')}>Configure Stripe</Button>}>
          Add your Stripe secret key to manage recurring invoices and reminders.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Hero />
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<ReceiptLongOutlinedIcon />}
            label="Open invoices"
            value={String(invoices.length)}
            color="#a78bfa"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<AutorenewOutlinedIcon />}
            label="Due in"
            value={`${billing.daysUntilDue} days`}
            color="#34d399"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<NotificationsActiveOutlinedIcon />}
            label="Reminders"
            value={billing.reminderEnabled ? billing.reminderDaysBefore.join(', ') + 'd before' : 'Off'}
            color="#f97316"
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New invoice
        </Button>
        <Button variant="outlined" startIcon={<AutorenewOutlinedIcon />} onClick={() => setSubOpen(true)}>
          Recurring subscription
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={busy === 'all' ? <CircularProgress size={18} /> : <NotificationsActiveOutlinedIcon />}
          onClick={() => void runReminders()}
          disabled={!billing.reminderEnabled}
        >
          Run all reminders
        </Button>
        <Button variant="text" startIcon={<SettingsOutlinedIcon />} onClick={() => navigate('/settings#stripe')}>
          Billing settings
        </Button>
      </Stack>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Open invoices" />
          <Tab label="How it works" />
        </Tabs>
        <CardContent>
          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Amount due</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" py={3} textAlign="center">
                        No open invoices — create one or set up a recurring subscription.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{inv.number || inv.id.slice(-8)}</Typography>
                      <Chip size="small" label={inv.status || 'open'} sx={{ mt: 0.5 }} />
                    </TableCell>
                    <TableCell>{inv.customer_name || inv.customer_email || '—'}</TableCell>
                    <TableCell>{fmtDate(inv.due_date)}</TableCell>
                    <TableCell align="right">{fmtMoney(inv.amount_due, inv.currency)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {inv.hosted_invoice_url && (
                          <Tooltip title="Open hosted invoice">
                            <IconButton size="small" href={inv.hosted_invoice_url} target="_blank">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {dashBase && (
                          <Tooltip title="Stripe dashboard">
                            <IconButton size="small" href={`${dashBase}/invoices/${inv.id}`} target="_blank">
                              <ReceiptLongOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={busy === inv.id ? <CircularProgress size={14} /> : <SendOutlinedIcon />}
                          onClick={() => void remind(inv.id)}
                        >
                          Remind
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tab === 1 && (
            <Stack spacing={2} maxWidth={640}>
              <Typography variant="body2" color="text.secondary">
                Growth Hub creates <strong>send_invoice</strong> subscriptions and one-off invoices in Stripe. When
                auto-send is on, invoices are finalized and emailed via Stripe immediately.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reminders use your schedule ({billing.reminderDaysBefore.join(', ')} days before due) via{' '}
                {billing.reminderChannel === 'both'
                  ? 'Stripe + Gmail SMTP'
                  : billing.reminderChannel === 'email'
                    ? 'Gmail SMTP'
                    : 'Stripe only'}
                . Configure SMTP under API Settings → Google Email.
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create & send invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Customer"
              fullWidth
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              helperText={`Currency: ${billing.defaultCurrency.toUpperCase()}`}
            />
            <TextField
              label="Description"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              void (async () => {
                try {
                  const cents = Math.round(parseFloat(form.amount) * 100);
                  await createAndSendInvoice(settings, {
                    customerId: form.customerId,
                    amountCents: cents,
                    description: form.description,
                  });
                  setCreateOpen(false);
                  setMessage({ type: 'success', text: 'Invoice created' });
                  void load();
                } catch (e) {
                  setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
                }
              })();
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subOpen} onClose={() => setSubOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recurring subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Customer"
              fullWidth
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Price (from Stripe)"
              fullWidth
              value={form.priceId}
              onChange={(e) => setForm((f) => ({ ...f, priceId: e.target.value }))}
            >
              {prices.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nickname} — {fmtMoney(p.amount, p.currency)}/{p.interval}
                </MenuItem>
              ))}
            </TextField>
            {prices.length === 0 && (
              <Alert severity="info">Create recurring Prices in Stripe Dashboard first.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.customerId || !form.priceId}
            onClick={() => {
              void (async () => {
                try {
                  await createRecurringSubscription(settings, form.customerId, form.priceId);
                  setSubOpen(false);
                  setMessage({ type: 'success', text: 'Subscription created — Stripe will invoice each period' });
                } catch (e) {
                  setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed' });
                }
              })();
            }}
          >
            Start subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function Hero() {
  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #7c3aed 100%)',
        color: '#fff',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <ReceiptLongOutlinedIcon sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={800}>
          Billing & reminders
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 560 }}>
        Raise recurring Stripe invoices, send payment reminders by email, and keep cash flow on track.
      </Typography>
    </Box>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}22`, color }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default BillingPage;
